import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { MoneyInput } from '../../../components/MoneyInput';
import { RadioRow } from './_shared';

export default function RetirementExpensesStep({ formData, updateFormData }) {
  const expenses = formData.expensesEnding || [];
  const updateSection = (patch) => updateFormData({ ...formData, ...patch });

  const addExpense = () => updateSection({ expensesEnding: [...expenses, { description: '', yearEnd: '', amount: '', inflate: 'no' }] });
  const removeExpense = (idx) => updateSection({ expensesEnding: expenses.filter((_, i) => i !== idx) });
  const updateExpense = (idx, field, value) => {
    const next = expenses.map((e, i) => (i === idx ? { ...e, [field]: value } : e));
    updateSection({ expensesEnding: next });
  };

  return (
    <div data-testid="retirement-step">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Retirement & Basic Living Expenses
      </h2>
      <p className="text-sm text-[#64748b] mb-6">Capture your current basic living expenses and any expenses that will end during retirement.</p>

      {/* Basic Living Expenses */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Basic Living Expenses</h3>
          <Button onClick={() => updateSection({ basicLivingExpenses: [...(formData.basicLivingExpenses || []), { description: '', amount: '', frequency: 'monthly' }] })} size="sm" variant="outline" data-testid="add-basic-expense-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        {(formData.basicLivingExpenses || []).map((exp, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Expense #{idx + 1}</span>
              <Button onClick={() => updateSection({ basicLivingExpenses: (formData.basicLivingExpenses || []).filter((_, i) => i !== idx) })} size="sm" variant="ghost" data-testid={`remove-basic-expense-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Description</Label>
                <Input placeholder="e.g., Mortgage, Groceries, Utilities" value={exp.description || ''} onChange={(e) => {
                  const next = (formData.basicLivingExpenses || []).map((it, i) => i === idx ? { ...it, description: e.target.value } : it);
                  updateSection({ basicLivingExpenses: next });
                }} className="mt-2" />
              </div>
              <div>
                <Label>Amount</Label>
                <MoneyInput placeholder="$0" value={exp.amount} onChange={(v) => {
                  const next = (formData.basicLivingExpenses || []).map((it, i) => i === idx ? { ...it, amount: v } : it);
                  updateSection({ basicLivingExpenses: next });
                }} className="mt-2" />
              </div>
              <div>
                <Label>Frequency</Label>
                <RadioRow
                  name={`basic-exp-freq-${idx}`}
                  value={exp.frequency || 'monthly'}
                  onChange={(v) => {
                    const next = (formData.basicLivingExpenses || []).map((it, i) => i === idx ? { ...it, frequency: v } : it);
                    updateSection({ basicLivingExpenses: next });
                  }}
                  options={[{ label: 'Monthly', value: 'monthly' }, { label: 'Annually', value: 'annually' }]}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expenses Ending at Retirement */}
      <div className="mb-8 pt-6 border-t border-[#e2e8f0]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Expenses Ending at Retirement</h3>
          <Button onClick={addExpense} size="sm" variant="outline" data-testid="add-expense-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        {expenses.map((exp, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Expense #{idx + 1}</span>
              <Button onClick={() => removeExpense(idx)} size="sm" variant="ghost" data-testid={`remove-expense-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Description</Label>
                <Input placeholder="Description" value={exp.description || ''} onChange={(e) => updateExpense(idx, 'description', e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label>Year Expense Will End</Label>
                <Input placeholder="YYYY" value={exp.yearEnd || ''} onChange={(e) => updateExpense(idx, 'yearEnd', e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label>Amount (Current Dollars)</Label>
                <MoneyInput placeholder="$0" value={exp.amount} onChange={(v) => updateExpense(idx, 'amount', v)} className="mt-2" />
              </div>
              <div>
                <Label>Inflate</Label>
                <RadioRow
                  name={`expense-inflate-${idx}`}
                  value={exp.inflate}
                  onChange={(v) => updateExpense(idx, 'inflate', v)}
                  options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Retirement Income Needed */}
      <div className="pt-6 border-t border-[#e2e8f0]">
        <h3 className="text-lg font-medium text-[#0f172a] mb-4">Retirement Income Needed</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="retirementIncome">Value</Label>
            <MoneyInput value={formData.retirementIncome} onChange={(v) => updateSection({ retirementIncome: v })} placeholder="$0" className="mt-2" testId="retirement-income-input" />
          </div>
          <div>
            <Label>Per</Label>
            <RadioRow
              name="retirementIncomePer"
              value={formData.retirementIncomePer}
              onChange={(v) => updateSection({ retirementIncomePer: v })}
              options={[{ label: 'Per Month', value: 'month' }, { label: 'Per Year', value: 'year' }]}
              testIdPrefix="income-per"
            />
          </div>
          <div>
            <Label>Inflate</Label>
            <RadioRow
              name="retirementIncomeInflate"
              value={formData.inflate}
              onChange={(v) => updateSection({ inflate: v })}
              options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]}
              testIdPrefix="income-inflate"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <Label>Base Inflation Rate</Label>
            <RadioRow
              name="baseInflationRateToggle"
              value={formData.baseInflationRateToggle}
              onChange={(v) => updateSection({ baseInflationRateToggle: v })}
              options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]}
              testIdPrefix="base-inflation"
            />
            <Input
              className="mt-3"
              value={formData.baseInflationRate || ''}
              onChange={(e) => updateSection({ baseInflationRate: e.target.value })}
              placeholder="e.g., 3.0 (%)"
              data-testid="base-inflation-rate-input"
            />
          </div>
          <div>
            <Label htmlFor="baseInflationPlus">Or base inflation rate +</Label>
            <Input id="baseInflationPlus" value={formData.baseInflationPlus || ''} onChange={(e) => updateSection({ baseInflationPlus: e.target.value })} placeholder="e.g. 0.5" className="mt-2" />
          </div>
        </div>
      </div>

      {/* Change States in Retirement */}
      <div className="pt-6 border-t border-[#e2e8f0] mt-6">
        <h3 className="text-lg font-medium text-[#0f172a] mb-4">Relocation</h3>
        <div className="space-y-4">
          <div>
            <Label>Will you change states in Retirement?</Label>
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2">
                <input type="radio" name="changeStates" value="yes" checked={formData.changeStates === 'yes'} onChange={(e) => updateSection({ changeStates: e.target.value })} data-testid="change-states-yes" />
                Yes
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="changeStates" value="no" checked={formData.changeStates === 'no'} onChange={(e) => updateSection({ changeStates: e.target.value })} data-testid="change-states-no" />
                No
              </label>
            </div>
          </div>
          {formData.changeStates === 'yes' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div>
                <Label htmlFor="moveState">State where you will move</Label>
                <Input id="moveState" value={formData.moveState || ''} onChange={(e) => updateSection({ moveState: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="whenMove">When will you Move?</Label>
                <Input id="whenMove" value={formData.whenMove || ''} onChange={(e) => updateSection({ whenMove: e.target.value })} placeholder="e.g., at retirement" className="mt-2" />
              </div>
              <div>
                <Label htmlFor="moveYear">Or Move in a Specific Year</Label>
                <Input id="moveYear" value={formData.moveYear || ''} onChange={(e) => updateSection({ moveYear: e.target.value })} placeholder="YYYY" className="mt-2" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
